// Location: src/pages/plan-summary/components/WeightJourneyChart.tsx
// ============================================

import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface WeightJourneyChartProps {
  startWeight: number;
  endWeight: number;
  goalWeight: number;
  planStartDate: string;
  durationDays: number;
}

export default function WeightJourneyChart({
  startWeight,
  endWeight,
  goalWeight,
  planStartDate,
  durationDays
}: WeightJourneyChartProps) {
  const { t, i18n } = useTranslation();
  const { planId } = useParams();
  const [weightData, setWeightData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadWeightHistory();
  }, [planId]);

  const loadWeightHistory = async () => {
    try {
      setIsLoading(true);

      const { data: logs, error } = await supabase
        .from('weight_history')
        .select('weight, created_at')
        .eq('current_plan_id', planId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!logs || logs.length === 0) {
        const data = generateEstimatedData(startWeight, endWeight, durationDays);
        setWeightData(data);
      } else {
        const data = logs.map((log, index) => ({
          day: index + 1,
          weight: log.weight,
          date: new Date(log.created_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
        }));
        setWeightData(data);
      }
    } catch (error) {
      console.error('Failed to load weight history:', error);
      const data = generateEstimatedData(startWeight, endWeight, durationDays);
      setWeightData(data);
    } finally {
      setIsLoading(false);
    }
  };

  const generateEstimatedData = (start: number, end: number, days: number) => {
    const data = [];
    const weightChange = end - start;
    const changePerDay = weightChange / days;

    data.push({
      day: 1,
      weight: start,
      date: new Date(planStartDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
    });

    for (let day = 7; day <= days; day += 7) {
      const estimatedWeight = start + (changePerDay * day);
      const date = new Date(planStartDate);
      date.setDate(date.getDate() + day);
      
      data.push({
        day,
        weight: estimatedWeight,
        date: date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
      });
    }

    if (data[data.length - 1].day !== days) {
      const endDate = new Date(planStartDate);
      endDate.setDate(endDate.getDate() + days);
      
      data.push({
        day: days,
        weight: end,
        date: endDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
      });
    }

    return data;
  };

  if (isLoading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 lg:p-8 backdrop-blur-sm flex flex-col items-center justify-center min-h-[400px]">
         <div className="w-8 h-8 border-t-2 border-yellow-400 rounded-full animate-spin mb-4"></div>
         <div className="text-gray-500 font-light tracking-wide">{t('planSummary.weightJourney.loading')}</div>
      </div>
    );
  }

  const minWeight = Math.min(startWeight, endWeight, goalWeight) - 2;
  const maxWeight = Math.max(startWeight, endWeight, goalWeight) + 2;

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 lg:p-8 relative overflow-hidden backdrop-blur-sm">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <h3 className="text-xl font-light text-white tracking-tight">{t('planSummary.weightJourney.title')}</h3>
          <p className="text-xs text-gray-500 mt-1">{t('planSummary.weightJourney.subtitle')}</p>
        </div>
        
        <div className="flex items-center gap-5 text-xs font-medium uppercase tracking-wider">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full shadow-[0_0_8px_rgba(250,204,21,0.6)]"></div>
            <span className="text-gray-400">{t('planSummary.weightJourney.legend.progress')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-px bg-green-500/70"></div>
            <span className="text-gray-400">{t('planSummary.weightJourney.legend.goal')}</span>
          </div>
        </div>
      </div>

      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={weightData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#EAB308" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#EAB308" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff" strokeOpacity={0.05} vertical={false} />
            <XAxis 
              dataKey="date" 
              stroke="#6B7280"
              style={{ fontSize: '11px', fontFamily: 'inherit' }}
              tickLine={false}
              axisLine={false}
              dy={10}
            />
            <YAxis 
              domain={[minWeight, maxWeight]}
              stroke="#6B7280"
              style={{ fontSize: '11px', fontFamily: 'inherit' }}
              tickFormatter={(value) => `${value}kg`}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(10, 10, 10, 0.8)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                color: '#fff',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)'
              }}
              itemStyle={{ color: '#EAB308', fontWeight: 500 }}
              formatter={(value: any) => [`${value.toFixed(1)} kg`, t('planSummary.weightJourney.tooltip.weight')]}
            />
            
            <ReferenceLine 
              y={goalWeight} 
              stroke="#10B981" 
              strokeDasharray="4 4"
              strokeOpacity={0.5}
            />
            
            <Area
              type="monotone"
              dataKey="weight"
              stroke="#EAB308"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorWeight)"
              activeDot={{ r: 6, fill: '#EAB308', stroke: '#000', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-white/5">
        <div>
          <div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('planSummary.weightJourney.summary.start')}</div>
          <div className="text-white font-light text-xl">{startWeight.toFixed(1)} <span className="text-xs text-gray-500">kg</span></div>
        </div>
        <div>
          <div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('planSummary.weightJourney.summary.end')}</div>
          <div className="text-yellow-400 font-light text-xl">{endWeight.toFixed(1)} <span className="text-xs text-yellow-500/50">kg</span></div>
        </div>
        <div>
          <div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('planSummary.weightJourney.summary.change')}</div>
          <div className={`font-light text-xl ${endWeight < startWeight ? 'text-green-400' : 'text-blue-400'}`}>
            {endWeight < startWeight ? '' : '+'}{(endWeight - startWeight).toFixed(1)} <span className="text-xs opacity-50">kg</span>
          </div>
        </div>
      </div>
    </div>
  );
}